/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: dispatchLocationApi.ts
 🗓️ Created: 2025-12-04
 🧩 Description:
    API client for Dispatch Locations
────────────────────────────────────────────────────────────────*/

import { api } from '../../../lib/api';
import type {
  DispatchLocation,
  DispatchLocationFormState,
  ListDispatchLocationsQuery,
  ListDispatchLocationsResponse,
} from '../types';

const BASE_URL = '/api/telephony/dispatch-locations';

/**
 * Fetch dispatch locations list
 */
export async function fetchDispatchLocations(
  query: ListDispatchLocationsQuery,
  search?: string,
  domainId?: number
): Promise<ListDispatchLocationsResponse> {
  const params = new URLSearchParams();

  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);
  if (search) params.append('search', search);
  if (query.enabled != null) params.append('enabled', query.enabled.toString());
  if (query.assignmentType) params.append('assignmentType', query.assignmentType);
  if (query.validated != null) params.append('validated', query.validated.toString());
  if (domainId != null) params.append('domainId', domainId.toString());

  const response = await api.get(`${BASE_URL}?${params.toString()}`);
  return response.data;
}

/**
 * Fetch dispatch location by ID
 */
export async function fetchDispatchLocationById(id: string): Promise<DispatchLocation> {
  const response = await api.get(`${BASE_URL}/${id}`);
  return response.data.data;
}

/**
 * Create dispatch location
 */
export async function createDispatchLocation(
  data: DispatchLocationFormState
): Promise<DispatchLocation> {
  const response = await api.post(BASE_URL, data);
  return response.data.data;
}

/**
 * Update dispatch location
 */
export async function updateDispatchLocation(
  id: string,
  data: Partial<DispatchLocationFormState>
): Promise<DispatchLocation> {
  const response = await api.put(`${BASE_URL}/${id}`, data);
  return response.data.data;
}

/**
 * Delete dispatch location
 */
export async function deleteDispatchLocation(id: string): Promise<void> {
  await api.delete(`${BASE_URL}/${id}`);
}

/**
 * Get assignment types
 */
export async function fetchAssignmentTypes(): Promise<{ value: string; label: string }[]> {
  const response = await api.get(`${BASE_URL}/meta/assignment-types`);
  return response.data.data;
}

